<template>
    <div v-if="expireTime">
        {{ reservationText }}
        <v-progress-linear :value="progressValue" :color="progressColor"></v-progress-linear>
    </div>
</template>

<script>
import {decodeJson2} from '../util.js'
export default {
    name: 'zoezi-checkout-reservation-time',
    props: ['items', 'disabled'],
    data: () => ({
        expireTime: null,
        now1s: new Date(),
        isLoading: true,
        timeout: null,
        interval: null
    }),
    computed: {
        reservationKeys() {
            return this.items?.filter(item => item.reservation).map(item => item.reservation) || [];
        },
        isExpired() {
            return this.expireTime && this.expireTime < this.now1s;
        },
        remainingTime() {
            if (!this.expireTime) return '';

            const diff = this.expireTime - this.now1s;

            if (diff <= 0) {
                return ''
            } else if (diff <= 60000) { // mindre än en minut
                const seconds = (diff / 1000).toFixed(0);
                if (seconds == 1) {
                    return '1 ' + this.$translate('second')
                }
                return seconds + ' ' + this.$translate('seconds');
            } else {
                const minutes = Math.floor(diff / 60000);
                if (minutes == 1) {
                    return '1 ' + this.$translate('minute')
                }
                return minutes + ' ' + this.$translate('minutes');
            }
        },
        reservationText() {
            if (!this.remainingTime) {
                return this.$translate('Reservation is expired')
            }
            return this.$translate('Booking is reserved for {0}').format(this.remainingTime)
        },
        progressValue() {
            if (!this.expireTime) return 0;

            const totalDurationMilliseconds = this.timeout * 60 * 1000;  // Total reservation duration in milliseconds
            const remainingDurationMilliseconds = this.expireTime - this.now1s;

            return (remainingDurationMilliseconds / totalDurationMilliseconds) * 100;
        },
        progressColor() {
            const remainingMinutes = Math.floor((this.expireTime - this.now1s) / 60000);

            if (remainingMinutes <= 1) {
                return 'red';
            } else if (remainingMinutes <= 5) {
                return 'orange';
            } else {
                return 'green';
            }
        }
    },
    methods: {
        async validateReservations() {
            if (this.disabled) {
                return;
            }

            if (this.reservationKeys.length == 0) {
                this.expireTime = null;
                return;
            }

            const response = await this.$api.post('/api/resource/reservation/validate', { key: this.reservationKeys });
            let data = await response.json();
            data = decodeJson2(data);
            if (this.disabled) {
                return;
            }

            if (data.expire_utc) {
                this.expireTime = new Date(data.expire_utc.getTime() - data.expire_utc.getTimezoneOffset() * 60000)
            }
            this.timeout = data.timeout;

            // If data.result is false, it means there are reservations which are not valid or has expired
            if (!data.result) {
                this.expireTime = Date.newFull('1970-01-01');
                let whatExpired = data.expired_keys.map(this.getItemTitle).filter(x => x).join(', ');
                if (whatExpired && !this.$store.state.errorDialog.show) {
                    // If no name found, the item was probably already removed
                    this.$store.commit('showErrorDialog', {title: this.$translate('Reservation expired'), text: this.$translate('The reservation for {0} expired').format(whatExpired)});
                }

                // Remove this key from all carts
                data.expired_keys.forEach(this.removeCartItem);

                this.$emit('timeout')
            }
        },
        getItemTitle(key) {
            const item = this.items.find(item => item.reservation == key);
            return item?.name;
        },
        removeCartItem(key) {
            const carts = this.$store.state.carts || {};
            for (const cart of Object.values(carts)) {
                // Find item with reservation == key and remove related items
                const item = cart.items.find(item => item.reservation == key);
                if (item) {
                    const toRemove = cart.items.filter(x => x.reservation == key || (item.tmp_id && x.tmp_id == item.tmp_id) || (item.tmp_id && x.rbooking_id == item.tmp_id));
                    toRemove.forEach(x => {
                        const index = cart.items.indexOf(x);
                        if (index >= 0) {
                            cart.items.splice(index, 1);
                        }
                    });
                }
            }
        }
    },
    watch: {
        reservationKeys: {
            immediate: true,
            handler() {
                this.validateReservations();
            }
        },
        isExpired() {
            if (this.isExpired) {
                this.validateReservations();
            }

            this.$emit('isExpired', this.isExpired)
        },
        '$store.state.now30s'() {
            this.validateReservations();
        }
    },
    mounted() {
        this.interval = setInterval(() => this.now1s = new Date(), 1000);
    },
    beforeDestroy() {
        clearInterval(this.interval);
    }
}
</script>
